// What the attachments UI allows, mirroring the api (PRD 20). The server
// refuses regardless; these exist so nobody is offered a button that can only
// fail.

import { BusinessRole, DocumentRole } from '../../../types/businessApi';
import type { AttachableType } from '../../../types/documentAttachables';

const KEEPERS: BusinessRole[] = ['owner', 'accountant', 'bookkeeper'];

/**
 * Who may put a document on a record, or take one off: whoever may change the
 * record itself (the api's DocumentEndpoints#linking_roles_for). An AP clerk
 * attaches a bill's PDF; an AR clerk an invoice's.
 */
export const ATTACH_ROLES: Record<AttachableType, BusinessRole[]> = {
  Bill: [...KEEPERS, 'ap_clerk'],
  Vendor: [...KEEPERS, 'ap_clerk'],
  Invoice: [...KEEPERS, 'ar_clerk'],
  Customer: [...KEEPERS, 'ar_clerk'],
  BankTransaction: KEEPERS,
  Reconciliation: KEEPERS,
  JournalEntry: ['owner', 'accountant'],
  // PRD 88 Wave 2: the locations' writers (LocationEndpoints::WRITE_ROLES)
  // while the business holds field service — less the dispatcher, whom PRD
  // 89's gate refuses on the core documents controller.
  ServiceLocation: [...KEEPERS, 'ar_clerk'],
  // PRD 90 Wave 1: the api's DOCUMENT_ROLES for a job (owner, AR clerk,
  // dispatcher) while the business holds field service — less the dispatcher
  // here, whom PRD 89's gate refuses on the core documents controller. The
  // dispatcher (and a technician, read-only) reach a job's files through the
  // job's own documents route instead.
  ServiceJob: ['owner', 'ar_clerk'],
  // PRD 111 Wave 5 (5a): the pool's own `linking_roles` — owner and accountant,
  // and only while its year is a draft and the business holds the module. The
  // last two the panel cannot see; the screen passes `writable` for them.
  CannabisCostPool: ['owner', 'accountant'],
  // PRD 106 §4.11: the api's EquipmentEndpoints::DOCUMENT_ROLES (owner, AR
  // clerk, dispatcher) less the dispatcher, as for a job.
  LocationEquipment: ['owner', 'ar_clerk'],
  JobPermit: ['owner', 'ar_clerk'],
  // PRD 103 §4.17: the api's PO roles (owner, accountant, bookkeeper, AP clerk).
  PurchaseOrder: ['owner', 'accountant', 'bookkeeper', 'ap_clerk'],
  PurchaseOrderReceipt: ['owner', 'accountant', 'bookkeeper', 'ap_clerk'],
  // PRD 110 §4.14: the api's ProjectEndpoints::PROJECT_EDIT_ROLES less the
  // dispatcher, whom PRD 89's gate refuses on the core documents controller.
  ServiceProject: ['owner', 'accountant', 'bookkeeper', 'ar_clerk'],
};

/** Who may delete from the library: a delete takes a document off every record. */
export const LIBRARY_ROLES: BusinessRole[] = KEEPERS;

export const canAttachTo = (type: AttachableType, role: BusinessRole | undefined): boolean =>
  !!role && ATTACH_ROLES[type].includes(role);

export const canManageLibrary = (role: BusinessRole | undefined): boolean =>
  !!role && LIBRARY_ROLES.includes(role);

/** How each record reads in a sentence ("Remove it from this bill"). */
export const RECORD_NOUNS: Record<AttachableType, string> = {
  Bill: 'bill',
  Vendor: 'vendor',
  Invoice: 'invoice',
  Customer: 'customer',
  BankTransaction: 'transaction',
  Reconciliation: 'reconciliation',
  JournalEntry: 'journal entry',
  ServiceLocation: 'location',
  ServiceJob: 'job',
  CannabisCostPool: 'cost pool',
  LocationEquipment: 'unit',
  JobPermit: 'permit',
  PurchaseOrder: 'purchase order',
  PurchaseOrderReceipt: 'receipt',
  ServiceProject: 'project',
};

export const ROLE_LABELS: Record<DocumentRole, string> = {
  receipt: 'Receipt',
  statement: 'Statement',
  contract: 'Contract',
  signed: 'Signed copy',
  supporting: 'Supporting document',
  other: 'Other',
};

/** The api's 25 MB (Documents::UploadService::MAX_BYTES). */
export const MAX_BYTES = 25 * 1024 * 1024;

/** The api's allowlist (Documents::FileSignature), for the file picker. */
export const ACCEPTED_EXTENSIONS = [
  '.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.heic',
  '.doc', '.docx', '.xls', '.xlsx', '.csv', '.txt',
];
export const ACCEPT = ACCEPTED_EXTENSIONS.join(',');

/**
 * Why a file will not be taken, before it is sent, or null. Only what the name
 * and size can say: what the bytes ARE is the server's to decide, so a renamed
 * file gets through here and is refused there, with its reason.
 */
export function fileRefusal(file: File): string | null {
  const dot = file.name.lastIndexOf('.');
  const extension = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  if (!ACCEPTED_EXTENSIONS.includes(extension)) {
    return `${file.name} is not a type attachments take: PDFs, images, Word, Excel, CSV and text are.`;
  }
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > MAX_BYTES) return `${file.name} is larger than 25 MB.`;
  return null;
}

/** "PDF", "Image", "Spreadsheet", from the type the api stored it as. */
export function kindLabel(contentType: string): string {
  if (contentType === 'application/pdf') return 'PDF';
  if (contentType.startsWith('image/')) return 'Image';
  if (contentType === 'text/csv' || contentType.includes('spreadsheet') || contentType.includes('ms-excel')) {
    return 'Spreadsheet';
  }
  if (contentType.includes('word')) return 'Word document';
  return 'Text';
}

/** What a browser shows without running anything; the api serves only these inline. */
export const INLINE_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export const opensInBrowser = (contentType: string): boolean => INLINE_TYPES.includes(contentType);

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
